import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { AUTH_ROUTES, auth0Config, AUTH_SCOPES } from '@feature/auth/constants/auth.constants';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { clearAuthApiFailure } from '@shared/utils/authApiFailure';
import {
  clearAuth0Storage,
  isAuth0SessionExpiredError,
  runSessionReauthOnce,
} from '../utils/authSession.utils';

const useRealAuth = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const { handleError } = useErrorHandler();

  const login = useCallback(async (customReturnTo?: string) => {
    try {
      const currentUrl = window.location.pathname + window.location.search;
      const returnTo = customReturnTo || (currentUrl === '/login' ? AUTH_ROUTES.HOME : currentUrl);

      await loginWithRedirect({
        appState: {
          returnTo: returnTo,
        },
      });
    } catch (error) {
      handleError(error, { showNotification: false, logToConsole: true });
    }
  }, [loginWithRedirect, handleError]);

  const logout = useCallback(async () => {
    try {
      clearAuthApiFailure();
      clearAuth0Storage();

      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
          federated: true,
        },
      });
    } catch (error) {
      handleError(error, { showNotification: false, logToConsole: true });
      clearAuth0Storage();
      window.location.href = window.location.origin;
    }
  }, [auth0Logout, handleError]);

  /**
   * Sesión no renovable en silencio (sin offline_access): limpiar cache y logout Auth0.
   * Sin federated para permitir SSO en el siguiente login si la cookie Auth0 sigue válida.
   */
  const reauthAfterSessionExpired = useCallback(async () => {
    await runSessionReauthOnce(async () => {
      clearAuthApiFailure();
      clearAuth0Storage();
      try {
        await auth0Logout({
          logoutParams: {
            returnTo: `${window.location.origin}${AUTH_ROUTES.LOGIN}`,
          },
        });
      } catch {
        window.location.href = `${window.location.origin}${AUTH_ROUTES.LOGIN}`;
      }
    });
  }, [auth0Logout]);

  const getToken = useCallback(async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
          scope: AUTH_SCOPES,
        },
      });
    } catch (error) {
      handleError(error, { showNotification: false, logToConsole: true });

      if (isAuth0SessionExpiredError(error)) {
        void reauthAfterSessionExpired();
      }

      // No devolver null: el interceptor no debe enviar requests sin Bearer.
      throw error;
    }
  }, [getAccessTokenSilently, handleError, reauthAfterSessionExpired]);

  if (error) {
    handleError(error, { showNotification: false, logToConsole: true });
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout,
    getToken,
  };
};

export const useAuth = useRealAuth;
